import { Context, useContext } from 'react';

/**
 * Creates a custom hook that consumes a React context. It allows the context to be used with or without a provider.
 *
 * @template V - The type of the context value.
 * @param context - The React context to create a hook for. The context's `displayName` must be set.
 */
export function createContextHook<V>(context: Context<V | undefined>) {
    if (!context.displayName) {
        throw new Error(
            'Context `displayName` is not set or is empty. Please set a non-empty `displayName` property on the context.',
        );
    }

    function useContextHook(options?: { isProviderOptional?: false }): V;
    function useContextHook(options: { isProviderOptional: true }): Partial<V>;
    function useContextHook(options: { isProviderOptional: boolean }): V | Partial<V>;
    function useContextHook(options?: {
        isProviderOptional?: boolean;
    }): V | Partial<V> {
        const value = useContext(context);

        if (options?.isProviderOptional) {
            return value ?? {};
        }

        if (value === undefined) {
            throw new Error(
                `${context.displayName} must be used within its provider component`,
            );
        }

        return value;
    }

    return useContextHook;
}
